import { useAuth } from "react-oidc-context";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  FilterParameters,
  parseLocationType,
  searchTeachers,
} from "../api/teacherSearchApi.ts";
import { useMemo } from "react";
import UserCard from "../components/UserCard.tsx";
import Pagination from "../components/Pagination.tsx";
import { useSearchParams } from "react-router";
import { useSupportedLanguages } from "../api/languageApi.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { LoadingSpinner } from "../components/util/LoadingSpinner.tsx";
import { SearchX } from "lucide-react";
import DashboardEmptyState from "../components/dashboard/DashboardEmptyState.tsx";

const PAGE_SIZE = 12;

export const Search = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter: FilterParameters = useMemo(() => {
    const latitude = searchParams.get("lat");
    const longitude = searchParams.get("long");
    const radius = searchParams.get("radius");
    const locationType = searchParams.get("locationType");
    return {
      name: searchParams.get("name"),
      languages: searchParams.getAll("languages"),
      instruments: searchParams.getAll("instrument"),
      genres: searchParams.getAll("genres"),
      filters: searchParams.getAll("filters"),
      latitude: latitude ? parseFloat(latitude) : null,
      longitude: longitude ? parseFloat(longitude) : null,
      radius: radius ? parseFloat(radius) : null,
      locationType: locationType ? parseLocationType(locationType) : null,
    };
  }, [searchParams]);
  const page = useMemo(() => {
    const pageParam = searchParams.get("page");
    if (!pageParam) {
      return 1;
    }
    return parseInt(pageParam);
  }, [searchParams]);
  const {
    data: teachers,
    isFetching,
    isSuccess,
    isLoading,
  } = useQuery({
    queryKey: ["userData", filter, page],
    queryFn: () => searchTeachers(user, filter, page - 1, PAGE_SIZE),
    placeholderData: keepPreviousData,
  });
  const { data: supportedLanguages } = useSupportedLanguages();
  const { t } = useLingui();

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", page.toString());
    setSearchParams(params);
  };

  // Initial loading state
  if (isLoading) {
    return <LoadingSpinner loadingText={t`Searching for teachers...`} />;
  }

  if (teachers) {
    const hasResults = teachers.results.length > 0;

    return (
      <div className="mx-auto max-w-7xl px-4 pt-6 pb-10 sm:px-6">
        {/* Results header */}
        {isSuccess && hasResults && (
          <div className="mb-6 flex items-center justify-between">
            <h1 className="text-soluud-text-primary text-lg font-semibold tracking-tight">
              <Trans>Teachers found</Trans>
              <span className="text-soluud-text-secondary ml-2 text-sm font-normal">
                ({teachers.results.length}
                {teachers.results.length >= PAGE_SIZE ? "+" : ""})
              </span>
            </h1>
          </div>
        )}

        <div className="relative">
          {/* Overlay spinner when refetching / paginating */}
          {isFetching && !isLoading && (
            <div className="pointer-events-none absolute inset-0 z-10 flex justify-center pt-20">
              <div className="relative h-8 w-8">
                <div className="border-soluud-primary/20 absolute inset-0 rounded-full border-4" />
                <div className="border-t-soluud-primary absolute inset-0 animate-spin rounded-full border-4 border-transparent" />
              </div>
            </div>
          )}

          {/* Empty state */}
          {isSuccess && !hasResults && (
            <div className="py-10">
              <DashboardEmptyState
                icon={SearchX}
                message={
                  <Trans>
                    Sorry, no matches found. Try adjusting your filters or
                    search for a different instrument.
                  </Trans>
                }
                linkTo="/search"
                linkLabel={<Trans>Reset Search</Trans>}
              />
            </div>
          )}

          {/* Results grid */}
          {hasResults && (
            <div
              className={`grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 ${
                isFetching ? "opacity-50" : ""
              }`}
            >
              {teachers.results.map((user) => (
                <UserCard
                  key={user.id + user.locationId}
                  user={user}
                  supportedLanguages={supportedLanguages}
                />
              ))}
            </div>
          )}

          {hasResults && (
            <Pagination
              currentPage={page}
              onPageChange={handlePageChange}
              pageRangeDisplayed={1}
              isLoading={isFetching}
              hasNextPage={teachers.results.length >= PAGE_SIZE}
            />
          )}
        </div>
      </div>
    );
  }

  // Error fallback
  return (
    <div className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6">
      <p className="text-soluud-text-secondary text-base">
        <Trans>Oops, could not load teachers. Please try again later.</Trans>
      </p>
    </div>
  );
};
